import { useState } from "react";
import { Link } from "react-router-dom";
import { Lockup } from "./Lockup";
import { Modal } from "./Modal";
import { useLang, useLp, useT } from "../lib/i18n";
import { getFooterColumns, type InternalLinkNode } from "../lib/seo/internalLinks";
import { LegalDocumentView } from "../legal/LegalDocument";
import { getPrivacy } from "../legal/privacy";
import { getTerms } from "../legal/terms";

/**
 * Footer with product identity + three column groups (compare, use cases,
 * resources). Compare and Use cases are driven entirely by
 * `lib/seo/internalLinks` so adding a new comparison or use-case page is
 * one entry there — no Footer edits and no hard-coded link list to drift.
 *
 * The Resources column also draws its real internal links (downloads / blog)
 * from the same graph; privacy and terms render as buttons that open in-page
 * modals (the dedicated /privacy and /terms routes still exist for direct
 * URLs, deep links, and SEO). `support` stays a literal mailto because it
 * isn't an indexable URL.
 */
export function Footer() {
  const t = useT();
  const lp = useLp();
  const lang = useLang();
  const { comparisons, useCases, resources } = getFooterColumns();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const privacyNode = resources.find((r) => r.id === "privacy") ?? null;
  const termsNode = resources.find((r) => r.id === "terms") ?? null;

  /** Trust page uses localized slugs per language tree. */
  const trustPath: Record<string, string> = {
    en: "/trust",
    pt: "/onde-seus-dados-vao",
    es: "/donde-van-tus-datos",
  };

  const linkClass =
    "text-[13px] text-ink/60 dark:text-paper/60 hover:text-ink dark:hover:text-paper transition-colors text-left";
  const headingClass =
    "font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45 dark:text-paper/45";

  const renderColumn = (title: string, items: InternalLinkNode[]) => (
    <div>
      <p className={headingClass}>{title}</p>
      <ul className="mt-3 space-y-2">
        {items.map((node) => (
          <li key={node.id}>
            <Link to={lp + node.path} className={linkClass}>
              {node.label[lang]}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className="border-t border-margin dark:border-paper/10">
      <div className="mx-auto max-w-6xl px-6 py-14">
        <div className="grid gap-10 sm:grid-cols-2 md:grid-cols-4">
          {/* Brand */}
          <div className="md:col-span-1">
            <Lockup className="h-6 w-auto" />
            <p className="mt-3 text-xs text-ink/50 dark:text-paper/50">
              © {new Date().getFullYear()} Minuta · {t.footer.tagline}
            </p>
            <div className="mt-4 flex gap-3">
              <a
                href="https://www.facebook.com/minutapp.tech/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-ink/50 dark:text-paper/50 hover:text-ink dark:hover:text-paper transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/minutapp.tech/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-ink/50 dark:text-paper/50 hover:text-ink dark:hover:text-paper transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
            </div>
          </div>

          {renderColumn(t.footer.compareHeading, comparisons)}
          {renderColumn(t.footer.useCasesHeading, useCases)}

          {/* Resources — graph-driven internal links + literal mailto support.
              Privacy and Terms render as buttons that open the in-page modal
              (the /privacy and /terms routes still resolve for direct URLs). */}
          <div>
            <p className={headingClass}>{t.footer.resourcesHeading}</p>
            <ul className="mt-3 space-y-2">
              {resources.map((node) => {
                if (node.id === "trust") {
                  return (
                    <li key={node.id}>
                      <Link to={lp + trustPath[lang]} className={linkClass}>
                        {node.label[lang]}
                      </Link>
                    </li>
                  );
                }
                if (node.id === "privacy") {
                  return (
                    <li key={node.id}>
                      <button
                        type="button"
                        onClick={() => setPrivacyOpen(true)}
                        className={linkClass}
                      >
                        {node.label[lang]}
                      </button>
                    </li>
                  );
                }
                if (node.id === "terms") {
                  return (
                    <li key={node.id}>
                      <button
                        type="button"
                        onClick={() => setTermsOpen(true)}
                        className={linkClass}
                      >
                        {node.label[lang]}
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={node.id}>
                    <Link to={lp + node.path} className={linkClass}>
                      {node.label[lang]}
                    </Link>
                  </li>
                );
              })}
              <li>
                <a href="mailto:support@minutapp.tech" className={linkClass}>
                  {t.footer.support}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {privacyNode && (
        <Modal
          open={privacyOpen}
          onClose={() => setPrivacyOpen(false)}
          title={privacyNode.label[lang]}
        >
          <LegalDocumentView doc={getPrivacy(lang)} />
        </Modal>
      )}
      {termsNode && (
        <Modal
          open={termsOpen}
          onClose={() => setTermsOpen(false)}
          title={termsNode.label[lang]}
        >
          <LegalDocumentView doc={getTerms(lang)} />
        </Modal>
      )}
    </footer>
  );
}
